import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import type { MarkerInfo, PromptRun, SearchResult } from '../../services/api';
import type { AgenticThreadMessage } from '../../features/agentic-search/types';
import { getSearchResultRunId } from '../../utils/searchResultIdentity';
import type { SearchWorkspaceView } from '../../contexts/SearchWorkspaceContext';
import SearchResultsGallery from './SearchResultsGallery';

export interface AgentResultsCanvasHandle {
  jumpTo: (turnId: string, resultSetId?: string | null) => boolean;
}

interface AgentResultsCanvasProps {
  messages: AgenticThreadMessage[];
  runs?: PromptRun[];
  view: SearchWorkspaceView;
  onResultClick: (result: SearchResult) => void;
  onMarkerChange?: (result: SearchResult, marker: MarkerInfo) => void;
  selectedResultKey?: string | null;
  selectedOccurrenceKey?: string | null;
  onResultOccurrenceClick?: (result: SearchResult, occurrenceKey: string) => void;
}

const messageHeading = (content: string): string => {
  const words = content.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean).slice(0, 8);
  const heading = words.join(' ');
  if (!heading) return 'Agent results';
  return heading.length > 80 ? `${heading.slice(0, 77)}…` : heading;
};

const dedupeWithinResultSet = (results: SearchResult[]): SearchResult[] => {
  const seen = new Set<string>();
  return results.filter((result) => {
    const key = [
      result.source_index_id || '',
      result.video_id,
      getSearchResultRunId(result) || '',
      result.segment_id || (result.result_type === 'video' ? 'video' : result.result_id),
    ].join('::');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const AgentResultsCanvas = forwardRef<AgentResultsCanvasHandle, AgentResultsCanvasProps>(({
  messages,
  runs,
  view,
  onResultClick,
  onMarkerChange,
  selectedResultKey,
  selectedOccurrenceKey,
  onResultOccurrenceClick,
}, ref) => {
  const anchorsRef = useRef(new Map<string, HTMLElement>());
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);
  const sections = useMemo(() => messages
    .filter((message): message is Extract<AgenticThreadMessage, { role: 'assistant' }> => (
      message.role === 'assistant'
    ))
    .map((message, index) => ({ message, index }))
    .sort((left, right) => {
      const leftTime = Date.parse(left.message.timestamp);
      const rightTime = Date.parse(right.message.timestamp);
      if (Number.isNaN(leftTime) || Number.isNaN(rightTime) || leftTime === rightTime) {
        return left.index - right.index;
      }
      return leftTime - rightTime;
    })
    .map(({ message }) => message)
    .map((message) => ({
      message,
      resultSets: message.resultSets
        .map((resultSet) => ({
          ...resultSet,
          results: dedupeWithinResultSet(resultSet.results || []),
        }))
        .filter((resultSet) => resultSet.results.length > 0),
    }))
    .filter((section) => section.resultSets.length > 0), [
      messages,
    ]);

  useEffect(() => () => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
  }, []);

  useImperativeHandle(ref, () => ({
    jumpTo: (turnId, resultSetId) => {
      const resultSetKey = resultSetId ? `${turnId}:${resultSetId}` : turnId;
      const resultSetTarget = anchorsRef.current.get(resultSetKey);
      const resolvedKey = resultSetTarget ? resultSetKey : turnId;
      const target = resultSetTarget || anchorsRef.current.get(turnId);
      if (!target) return false;
      const reduceMotion = typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      target.focus({ preventScroll: true });
      setHighlightedKey(resolvedKey);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(() => setHighlightedKey(null), 2000);
      return true;
    },
  }), []);

  if (sections.length === 0) {
    return (
      <div className="search-agent-results-empty">
        <MessageSquare size={42} />
        <h2>Conversation results appear here</h2>
      </div>
    );
  }

  return (
    <div className="search-agent-result-sections">
      {sections.map(({ message, resultSets }) => {
        const sectionKey = message.turnId;
        const resultCount = resultSets.reduce((total, resultSet) => total + resultSet.results.length, 0);
        const heading = messageHeading(message.content);
        return (
          <section
            key={message.id}
            ref={(node) => {
              if (node) anchorsRef.current.set(sectionKey, node);
              else anchorsRef.current.delete(sectionKey);
            }}
            className={`search-agent-result-section ${highlightedKey === sectionKey ? 'highlighted' : ''}`}
            tabIndex={-1}
            data-agent-turn={message.turnId}
            data-testid="agent-result-section"
            aria-label={`${heading} agent results`}
          >
            <header className="search-agent-result-separator">
              <span>{heading}</span>
              <strong>{resultCount} result{resultCount === 1 ? '' : 's'}</strong>
            </header>
            {resultSets.map((resultSet) => {
              const resultSetKey = `${message.turnId}:${resultSet.result_set_id}`;
              return (
                <section
                  key={resultSet.result_set_id}
                  ref={(node) => {
                    if (node) anchorsRef.current.set(resultSetKey, node);
                    else anchorsRef.current.delete(resultSetKey);
                  }}
                  className={`search-agent-result-set ${highlightedKey === resultSetKey ? 'highlighted' : ''}`}
                  tabIndex={-1}
                  data-agent-result-set={resultSet.result_set_id}
                  data-testid="agent-result-set"
                  aria-label={`${resultSet.label || 'Results'} result set`}
                >
                  <div className="search-agent-result-set-title">
                    <h3>{resultSet.label || 'Results'}</h3>
                    <span>{resultSet.results.length}</span>
                  </div>
                  <SearchResultsGallery
                    results={resultSet.results}
                    runs={runs}
                    view={view}
                    onResultClick={onResultClick}
                    onMarkerChange={onMarkerChange}
                    selectedResultKey={selectedResultKey}
                    occurrenceContext={`${message.turnId}:${resultSet.result_set_id}`}
                    selectedOccurrenceKey={selectedOccurrenceKey}
                    onResultOccurrenceClick={onResultOccurrenceClick}
                  />
                </section>
              );
            })}
          </section>
        );
      })}
    </div>
  );
});

AgentResultsCanvas.displayName = 'AgentResultsCanvas';

export default AgentResultsCanvas;
